/**
 * Centralized API Client
 *
 * A reusable Axios-based API client for all backend interactions.
 * Features:
 * - Axios instance with baseURL from environment variables
 * - JWT authentication interceptors
 * - Comprehensive error handling
 * - TypeScript generics support (via JSDoc)
 * - Request/response interceptors
 * - Automatic token refresh
 * - Retry logic for failed requests
 */

import { attachAuthRefreshInterceptor, createHttpClient } from '../api/http';
import { getApiBaseUrl } from '../config/api';

import errorLogger from './errorLoggingService';

const resolveBaseUrl = (envVal, defaultPort = '') => {
  if (envVal && !envVal.includes('localhost')) return envVal;
  if (
    typeof window !== 'undefined' &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1'
  ) {
    return window.location.origin + defaultPort;
  }
  return envVal || `http://${'localhost'}:8000${defaultPort}`;
};

// Environment variables with fallbacks
// Use relative path - Nginx will proxy /api to backend
const API_BASE_URL = getApiBaseUrl();
const ML_SERVICE_URL = resolveBaseUrl(
  import.meta.env.VITE_ML_SERVICE_URL || `http://${'localhost'}:8002`
);
const HITL_PORTAL_URL = resolveBaseUrl(
  import.meta.env.VITE_HITL_PORTAL_URL || `http://${'localhost'}:8000`
);

// API endpoints configuration
export const API_ENDPOINTS = {
  // Main Service (port 8000)
  AUTH: '/api/v1/auth',
  USERS: '/api/v1/admin/users',
  KYC: '/api/v1/kyc',
  CAMERAS: '/api/v1/cameras',
  ADMIN_CAMERAS: '/api/v1/admin/cameras',
  HITL: '/api/v1/hitl',
  CHATBOT: '/api/v1/chatbot',
  CHATBOT_MESSAGE_FEEDBACK: messageId =>
    `/api/v1/chatbot/message/${messageId}/feedback`,
  ANALYTICS: '/api/v1/admin/analytics',
  CALL_MODULE: '/api/v1/calls',

  // ML Service (port 8002)
  ML: '/api/v1/ml',

  // HITL Portal (port 8081 - if separate, but currently handled by main)
  HITL_PORTAL: '',
};

// Shared per-client options: api.js semantics — preserve a pre-set
// Authorization header, forward X-Tenant-ID from localStorage, attach
// request metadata (requestId/startTime) with debug logging.
const facadeClientOptions = {
  headers: { Accept: 'application/json' },
  tenantHeader: true,
  requestMetadata: true,
};

/**
 * Create Axios instance with default configuration
 */
const apiClient = createHttpClient({
  baseURL: API_BASE_URL,
  name: 'main-api',
  timeout: 30000,
  ...facadeClientOptions,
});

// Unauthenticated client for the token-refresh endpoint (no interceptors).
const refreshApiClient = createHttpClient({
  baseURL: API_BASE_URL || '',
  timeout: 30000,
  headers: { Accept: 'application/json' },
  auth: false,
});

/**
 * ML Service API client
 */
const mlApiClient = createHttpClient({
  baseURL: ML_SERVICE_URL,
  name: 'ml-api',
  timeout: 60000,
  ...facadeClientOptions,
});

/**
 * HITL Portal API client (Legacy - redirects to main port 8000 by default)
 */
const hitlApiClient = createHttpClient({
  baseURL: HITL_PORTAL_URL,
  name: 'hitl-api',
  timeout: 30000,
  ...facadeClientOptions,
});

// Attach the canonical 401-refresh response interceptor (api.js
// semantics: duration logging, 5xx error reporting, logout+redirect on
// unrecoverable 401, user-facing 403/5xx/network messages).
const facadeRefreshOptions = {
  logging: true,
  errorLogger,
  onAuthFailure: 'logout-redirect',
  normalizeMessages: true,
};

attachAuthRefreshInterceptor(apiClient, {
  name: 'main-api',
  ...facadeRefreshOptions,
});
attachAuthRefreshInterceptor(mlApiClient, {
  name: 'ml-api',
  ...facadeRefreshOptions,
});
attachAuthRefreshInterceptor(hitlApiClient, {
  name: 'hitl-api',
  ...facadeRefreshOptions,
});

/**
 * Generic API response type
 * @template T
 * @typedef {Object} ApiResponse
 * @property {T} data - Response data
 * @property {boolean} success - Success status
 * @property {string} [message] - Optional message
 * @property {Object} [meta] - Metadata
 */

/**
 * API Error type
 * @typedef {Object} ApiError
 * @property {string} message - Error message
 * @property {number} [status] - HTTP status code
 * @property {Object} [data] - Additional error data
 */

/**
 * Authentication API methods
 */
export const authAPI = {
  /**
   * Login user
   * @param {Object} credentials
   * @param {string} credentials.username
   * @param {string} credentials.password
   * @returns {Promise<ApiResponse<{access_token: string, refresh_token: string, user_id: string}>>}
   */
  async login(credentials) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.AUTH}/login`,
      credentials
    );
    return response.data;
  },

  /**
   * Refresh access token
   * @param {string} refreshToken
   * @returns {Promise<ApiResponse<{access_token: string, refresh_token: string}>>}
   */
  async refreshToken(refreshToken) {
    const response = await refreshApiClient.post(
      `${API_ENDPOINTS.AUTH}/refresh`,
      {
        refresh_token: refreshToken,
      }
    );
    return response.data;
  },

  /**
   * Logout user
   * @returns {Promise<ApiResponse<{message: string}>>}
   */
  async logout() {
    const response = await apiClient.post(`${API_ENDPOINTS.AUTH}/logout`);
    return response.data;
  },
};

/**
 * User management API methods
 */
export const userAPI = {
  /**
   * Get current user profile
   * @returns {Promise<ApiResponse<User>>}
   */
  async getCurrentUser() {
    const response = await apiClient.get(`${API_ENDPOINTS.AUTH}/me`);
    return response.data;
  },

  /**
   * Get all users
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<User[]>>}
   */
  async getUsers(params = {}) {
    const response = await apiClient.get(API_ENDPOINTS.USERS, { params });
    return response.data;
  },

  /**
   * Get user by ID
   * @param {string} userId
   * @returns {Promise<ApiResponse<User>>}
   */
  async getUser(userId) {
    const response = await apiClient.get(`${API_ENDPOINTS.USERS}/${userId}`);
    return response.data;
  },

  /**
   * Create new user
   * @param {Object} userData
   * @returns {Promise<ApiResponse<User>>}
   */
  async createUser(userData) {
    const response = await apiClient.post(API_ENDPOINTS.USERS, userData);
    return response.data;
  },

  /**
   * Update user
   * @param {string} userId
   * @param {Object} userData
   * @returns {Promise<ApiResponse<User>>}
   */
  async updateUser(userId, userData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.USERS}/${userId}`,
      userData
    );
    return response.data;
  },

  /**
   * Delete user
   * @param {string} userId
   * @returns {Promise<ApiResponse<{message: string}>>}
   */
  async deleteUser(userId) {
    const response = await apiClient.delete(`${API_ENDPOINTS.USERS}/${userId}`);
    return response.data;
  },
};

/**
 * KYC API methods
 */
export const kycAPI = {
  /**
   * Get KYC verification requests
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<KYCVerification[]>>}
   */
  async getVerifications(params = {}) {
    const response = await apiClient.get(API_ENDPOINTS.KYC, { params });
    return response.data;
  },

  /**
   * Get KYC verification by ID
   * @param {string} verificationId
   * @returns {Promise<ApiResponse<KYCVerification>>}
   */
  async getVerification(verificationId) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.KYC}/${verificationId}`
    );
    return response.data;
  },

  /**
   * Update KYC verification status
   * @param {string} verificationId
   * @param {Object} updateData
   * @returns {Promise<ApiResponse<KYCVerification>>}
   */
  async updateVerification(verificationId, updateData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.KYC}/${verificationId}`,
      updateData
    );
    return response.data;
  },

  /**
   * Get KYC statistics
   * @returns {Promise<ApiResponse<KYCStats>>}
   */
  async getStatistics() {
    const response = await apiClient.get(`${API_ENDPOINTS.KYC}/statistics`);
    return response.data;
  },
};

/**
 * Camera management API methods
 */
export const cameraAPI = {
  /**
   * Get all cameras
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<Camera[]>>}
   */
  async getCameras(params = {}) {
    const response = await apiClient.get(API_ENDPOINTS.CAMERAS, { params });
    return response.data;
  },

  /**
   * Get camera by ID
   * @param {string} cameraId
   * @returns {Promise<ApiResponse<Camera>>}
   */
  async getCamera(cameraId) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.CAMERAS}/${cameraId}`
    );
    return response.data;
  },

  /**
   * Create new camera
   * @param {Object} cameraData
   * @returns {Promise<ApiResponse<Camera>>}
   */
  async createCamera(cameraData) {
    const response = await apiClient.post(API_ENDPOINTS.CAMERAS, cameraData);
    return response.data;
  },

  /**
   * Update camera
   * @param {string} cameraId
   * @param {Object} cameraData
   * @returns {Promise<ApiResponse<Camera>>}
   */
  async updateCamera(cameraId, cameraData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.CAMERAS}/${cameraId}`,
      cameraData
    );
    return response.data;
  },

  /**
   * Delete camera
   * @param {string} cameraId
   * @returns {Promise<ApiResponse<{message: string}>>}
   */
  async deleteCamera(cameraId) {
    const response = await apiClient.delete(
      `${API_ENDPOINTS.CAMERAS}/${cameraId}`
    );
    return response.data;
  },

  // Admin Camera Management Methods
  /**
   * Get all cameras (admin)
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<Camera[]>>}
   */
  async getAdminCameras(params = {}) {
    const response = await apiClient.get(API_ENDPOINTS.ADMIN_CAMERAS, {
      params,
    });
    return response.data;
  },

  /**
   * Get camera by ID (admin)
   * @param {string} cameraId
   * @returns {Promise<ApiResponse<Camera>>}
   */
  async getAdminCamera(cameraId) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}`
    );
    return response.data;
  },

  /**
   * Create new camera (admin)
   * @param {Object} cameraData
   * @returns {Promise<ApiResponse<Camera>>}
   */
  async createAdminCamera(cameraData) {
    const response = await apiClient.post(
      API_ENDPOINTS.ADMIN_CAMERAS,
      cameraData
    );
    return response.data;
  },

  /**
   * Update camera (admin)
   * @param {string} cameraId
   * @param {Object} cameraData
   * @returns {Promise<ApiResponse<Camera>>}
   */
  async updateAdminCamera(cameraId, cameraData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}`,
      cameraData
    );
    return response.data;
  },

  /**
   * Delete camera (admin)
   * @param {string} cameraId
   * @returns {Promise<ApiResponse<{message: string}>>}
   */
  async deleteAdminCamera(cameraId) {
    const response = await apiClient.delete(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}`
    );
    return response.data;
  },

  /**
   * Update camera status (admin)
   * @param {string} cameraId
   * @param {Object} statusData
   * @returns {Promise<ApiResponse<Camera>>}
   */
  async updateCameraStatus(cameraId, statusData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/status`,
      statusData
    );
    return response.data;
  },

  /**
   * Bulk update camera status (admin)
   * @param {Object} bulkData
   * @returns {Promise<ApiResponse<{updated: number, failed: number}>>}
   */
  async bulkUpdateCameraStatus(bulkData) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/bulk-status`,
      bulkData
    );
    return response.data;
  },

  /**
   * Bulk update camera credentials (admin)
   * @param {Object} bulkData
   * @returns {Promise<ApiResponse<{updated: number, failed: number}>>}
   */
  async bulkUpdateCameraCredentials(bulkData) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/bulk-credentials`,
      bulkData
    );
    return response.data;
  },

  /**
   * Get camera statistics (admin)
   * @returns {Promise<ApiResponse<CameraStats>>}
   */
  async getCameraStats() {
    const response = await apiClient.get(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/stats`
    );
    return response.data;
  },

  /**
   * Get camera health (admin)
   * @param {string} cameraId
   * @returns {Promise<ApiResponse<CameraHealth>>}
   */
  async getCameraHealth(cameraId) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/health`
    );
    return response.data;
  },

  /**
   * Get camera events (admin)
   * @param {string} cameraId
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<CameraEvent[]>>}
   */
  async getCameraEvents(cameraId, params = {}) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/events`,
      { params }
    );
    return response.data;
  },

  /**
   * Test camera connection (admin)
   * @param {string} cameraId
   * @returns {Promise<ApiResponse<{success: boolean, message: string}>>}
   */
  async testCameraConnection(cameraId) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/test-connection`
    );
    return response.data;
  },

  /**
   * Get camera configuration (admin)
   * @param {string} cameraId
   * @returns {Promise<ApiResponse<CameraConfiguration>>}
   */
  async getCameraConfiguration(cameraId) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/configuration`
    );
    return response.data;
  },

  /**
   * Update camera configuration (admin)
   * @param {string} cameraId
   * @param {Object} configurationData
   * @returns {Promise<ApiResponse<CameraConfiguration>>}
   */
  async updateCameraConfiguration(cameraId, configurationData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/configuration`,
      configurationData
    );
    return response.data;
  },

  /**
   * Get camera location (admin)
   * @param {string} cameraId
   * @returns {Promise<ApiResponse<CameraLocation>>}
   */
  async getCameraLocation(cameraId) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/location`
    );
    return response.data;
  },

  /**
   * Update camera location (admin)
   * @param {string} cameraId
   * @param {Object} locationData
   * @returns {Promise<ApiResponse<CameraLocation>>}
   */
  async updateCameraLocation(cameraId, locationData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/location`,
      locationData
    );
    return response.data;
  },

  /**
   * Get camera configuration history
   * @param {string} cameraId
   * @returns {Promise<ApiResponse<CameraConfigurationHistory[]>>}
   */
  async getCameraConfigurationHistory(cameraId) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/configuration/history`
    );
    return response.data;
  },

  /**
   * Validate camera configuration
   * @param {string} cameraId
   * @param {Object} configuration
   * @returns {Promise<ApiResponse<ValidationResult>>}
   */
  async validateCameraConfiguration(cameraId, configuration) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/configuration/validate`,
      configuration
    );
    return response.data;
  },

  /**
   * Reset camera configuration to defaults
   * @param {string} cameraId
   * @returns {Promise<ApiResponse<CameraConfiguration>>}
   */
  async resetCameraConfiguration(cameraId) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.ADMIN_CAMERAS}/${cameraId}/configuration/reset`
    );
    return response.data;
  },
};

/**
 * HITL API methods
 */
export const hitlAPI = {
  /**
   * Get HITL statistics
   * @returns {Promise<ApiResponse<HITLStats>>}
   */
  async getStatistics() {
    const response = await apiClient.get(`${API_ENDPOINTS.HITL}/statistics`);
    return response.data;
  },

  /**
   * Get HITL data items
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<HITLDataItem[]>>}
   */
  async getData(params = {}) {
    const response = await apiClient.get(`${API_ENDPOINTS.HITL}/data`, {
      params,
    });
    return response.data;
  },

  /**
   * Get HITL data item by ID
   * @param {string} itemId
   * @returns {Promise<ApiResponse<HITLDataItem>>}
   */
  async getDataItem(itemId) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.HITL}/data/${itemId}`
    );
    return response.data;
  },

  /**
   * Update HITL data item
   * @param {string} itemId
   * @param {Object} updateData
   * @returns {Promise<ApiResponse<HITLDataItem>>}
   */
  async updateDataItem(itemId, updateData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.HITL}/data/${itemId}`,
      updateData
    );
    return response.data;
  },

  /**
   * Upload HITL data file
   * @param {FormData} formData
   * @returns {Promise<ApiResponse<HITLDataItem>>}
   */
  async uploadData(formData) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.HITL}/data/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Delete HITL data item
   * @param {string} itemId
   * @returns {Promise<ApiResponse<{message: string}>>}
   */
  async deleteDataItem(itemId) {
    const response = await apiClient.delete(
      `${API_ENDPOINTS.HITL}/data/${itemId}`
    );
    return response.data;
  },

  /**
   * Get file URL for HITL data item
   * @param {string} itemId
   * @param {Object} [params] - Query parameters (expiration, etc.)
   * @returns {Promise<ApiResponse<{url: string}>>}
   */
  async getFileURL(itemId, params = {}) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.HITL}/data/${itemId}/url`,
      { params }
    );
    return response.data;
  },

  /**
   * Get training batches
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<TrainingBatch[]>>}
   */
  async getTrainingBatches(params = {}) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.HITL}/training/batches`,
      {
        params,
      }
    );
    return response.data;
  },

  /**
   * Create training batch
   * @param {Object} batchData
   * @returns {Promise<ApiResponse<TrainingBatch>>}
   */
  async createTrainingBatch(batchData) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.HITL}/training/batches`,
      batchData
    );
    return response.data;
  },

  /**
   * Update training batch
   * @param {string} batchId - Training batch ID
   * @param {Object} updateData - Update data
   * @returns {Promise<ApiResponse<TrainingBatch>>}
   */
  async updateTrainingBatch(batchId, updateData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.HITL}/training/batches/${batchId}`,
      updateData
    );
    return response.data;
  },

  /**
   * Delete training batch
   * @param {string} batchId - Training batch ID
   * @returns {Promise<ApiResponse<{message: string}>>}
   */
  async deleteTrainingBatch(batchId) {
    const response = await apiClient.delete(
      `${API_ENDPOINTS.HITL}/training/batches/${batchId}`
    );
    return response.data;
  },

  /**
   * Get QA review items
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<QAReviewItem[]>>}
   */
  async getQAReviews(params = {}) {
    const response = await apiClient.get(`${API_ENDPOINTS.HITL}/qa`, {
      params,
    });
    return response.data;
  },

  /**
   * Update QA review
   * @param {string} reviewId
   * @param {Object} reviewData
   * @returns {Promise<ApiResponse<QAReviewItem>>}
   */
  async updateQAReview(reviewId, reviewData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.HITL}/qa/${reviewId}`,
      reviewData
    );
    return response.data;
  },

  /**
   * Get QA metrics
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<QAMetrics>>}
   */
  async getQAMetrics(params = {}) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.HITL}/quality/metrics`,
      {
        params,
      }
    );
    return response.data;
  },

  /**
   * Get pending QA reviews
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<{reviews: QAReviewItem[]}>>}
   */
  async getPendingReviews(params = {}) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.HITL}/quality/reviews/pending`,
      { params }
    );
    return response.data;
  },

  /**
   * Submit QA review
   * @param {string} reviewId - Review ID
   * @param {Object} reviewData - Review data
   * @returns {Promise<ApiResponse<QAReviewItem>>}
   */
  async submitQAReview(reviewId, reviewData) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.HITL}/quality/reviews/${reviewId}`,
      reviewData
    );
    return response.data;
  },

  // ==================== Session Management ====================

  /**
   * Start new labeling session
   * @returns {Promise<ApiResponse<LabelingSession>>}
   */
  async startLabelingSession() {
    const response = await apiClient.post(`${API_ENDPOINTS.HITL}/sessions`);
    return response.data;
  },

  /**
   * End labeling session
   * @param {string} sessionId - Session ID
   * @returns {Promise<ApiResponse<{message: string}>>}
   */
  async endLabelingSession(sessionId) {
    const response = await apiClient.put(
      `${API_ENDPOINTS.HITL}/sessions/${sessionId}/end`
    );
    return response.data;
  },

  // ==================== Data Labeling ====================

  /**
   * Get next data item for labeling
   * @returns {Promise<ApiResponse<HITLDataItem>>}
   */
  async getNextDataItem() {
    const response = await apiClient.get(`${API_ENDPOINTS.HITL}/labeling/next`);
    return response.data;
  },

  /**
   * Submit label for data item
   * @param {Object} labelData - Label data
   * @returns {Promise<ApiResponse<LabelingResult>>}
   */
  async submitLabel(labelData) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.HITL}/labeling/submit`,
      labelData
    );
    return response.data;
  },

  /**
   * Skip data item
   * @param {string} itemId - Data item ID
   * @returns {Promise<ApiResponse<{message: string}>>}
   */
  async skipDataItem(itemId) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.HITL}/labeling/${itemId}/skip`
    );
    return response.data;
  },

  /**
   * Get labeling history for current user
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<LabelingHistory[]>>}
   */
  async getLabelingHistory(params = {}) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.HITL}/labeling/history`,
      { params }
    );
    return response.data;
  },

  // ==================== Enhanced QA Methods ====================

  /**
   * Create new QA review
   * @param {Object} reviewData - Review data
   * @returns {Promise<ApiResponse<QAReviewItem>>}
   */
  async createQAReview(reviewData) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.HITL}/quality/reviews`,
      reviewData
    );
    return response.data;
  },

  /**
   * Assign reviews to reviewer
   * @param {string} reviewerId - Reviewer ID
   * @param {string[]} reviewIds - Array of review IDs
   * @returns {Promise<ApiResponse<{assigned_count: number}>>}
   */
  async assignReviews(reviewerId, reviewIds) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.HITL}/quality/reviews/assign/${reviewerId}`,
      {
        review_ids: reviewIds,
      }
    );
    return response.data;
  },

  /**
   * Get annotator performance metrics
   * @param {Object} [params] - Query parameters
   * @returns {Promise<ApiResponse<AnnotatorPerformance[]>>}
   */
  async getAnnotatorPerformance(params = {}) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.HITL}/quality/performance`,
      { params }
    );
    return response.data;
  },

  // ==================== Enhanced Training Methods ====================

  /**
   * Get specific training batch
   * @param {string} batchId - Training batch ID
   * @returns {Promise<ApiResponse<TrainingBatch>>}
   */
  async getTrainingBatch(batchId) {
    const response = await apiClient.get(
      `${API_ENDPOINTS.HITL}/training/batches/${batchId}`
    );
    return response.data;
  },

  /**
   * Export training data
   * @param {Object} exportData - Export configuration
   * @returns {Promise<ApiResponse<{download_url: string}>>}
   */
  async exportTrainingData(exportData) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.HITL}/training/export`,
      exportData
    );
    return response.data;
  },

  /**
   * Trigger model training
   * @param {Object} trainingData - Training configuration
   * @returns {Promise<ApiResponse<{job_id: string}>>}
   */
  async triggerTraining(trainingData) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.HITL}/training/trigger`,
      trainingData
    );
    return response.data;
  },

  /**
   * Handle training results
   * @param {string} batchId - Training batch ID
   * @param {Object} resultData - Training result data
   * @returns {Promise<ApiResponse<{message: string}>>}
   */
  async handleTrainingResult(batchId, resultData) {
    const response = await apiClient.post(
      `${API_ENDPOINTS.HITL}/training/results/${batchId}`,
      resultData
    );
    return response.data;
  },
};

/**
 * ML Service API methods
 */
export const mlAPI = {
  /**
   * Get ML model status
   * @returns {Promise<ApiResponse<ModelStatus>>}
   */
  async getModelStatus() {
    const response = await mlApiClient.get(`${API_ENDPOINTS.ML}/status`);
    return response.data;
  },

  /**
   * Trigger model training
   * @param {Object} trainingConfig
   * @returns {Promise<ApiResponse<TrainingJob>>}
   */
  async startTraining(trainingConfig) {
    const response = await mlApiClient.post(
      `${API_ENDPOINTS.ML}/train`,
      trainingConfig
    );
    return response.data;
  },

  /**
   * Get training job status
   * @param {string} jobId
   * @returns {Promise<ApiResponse<TrainingJob>>}
   */
  async getTrainingStatus(jobId) {
    const response = await mlApiClient.get(
      `${API_ENDPOINTS.ML}/train/${jobId}`
    );
    return response.data;
  },
};

/**
 * Dashboard API methods
 */
export const dashboardAPI = {
  /**
   * Get dashboard statistics
   * @returns {Promise<ApiResponse<DashboardStats>>}
   */
  async getStatistics() {
    const response = await apiClient.get('/api/v1/dashboard/statistics');
    return response.data;
  },

  /**
   * Get recent activities
   * @param {number} [limit=10]
   * @returns {Promise<ApiResponse<Activity[]>>}
   */
  async getRecentActivities(limit = 10) {
    const response = await apiClient.get('/api/v1/dashboard/activities', {
      params: { limit },
    });
    return response.data;
  },
};

// Export clients for advanced usage
export { apiClient, mlApiClient, hitlApiClient };

// Export configuration
export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  ML_SERVICE_URL,
  HITL_PORTAL_URL,
  ENDPOINTS: API_ENDPOINTS,
};

// Default export - main API object
export default {
  // Configuration
  config: API_CONFIG,

  // API modules
  auth: authAPI,
  users: userAPI,
  kyc: kycAPI,
  cameras: cameraAPI,
  hitl: hitlAPI,
  ml: mlAPI,
  dashboard: dashboardAPI,
};
